import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NotificationPanelService } from '@core/services';
import { TranslateDecoratorService } from '@core/translate-decorator.service';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { CrudOperation } from '@shared/models';
import { AppNotificationService, HeaderService } from '@shared/services';
import { cloneDeep } from 'lodash-es';
import { take } from 'rxjs';
import {
  EntityType,
  GeneralRatesRuleEngine,
  SecurityType,
} from 'src/app/modules/admin/models';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';
import { GeneralRatesRuleEngineService } from 'src/app/modules/admin/services';
import {
  CreateSecurityRequest,
  CreateSecurityTaxRateRequest,
  SecurityResponseAuditable,
  UpdateSecurityRequest,
} from 'src/app/web-api-client';
import { SecurityForm } from '../../abstractions/security-form';
import { UpsertSecurityState } from '../../models/upsert-security-state';
import { SecurityService } from '../../services/security.service';
import { RulesEngineViewComponent } from '../rules-engine-view/rules-engine-view.component';

@Component({
  standalone: false,
  selector: 'gstar-upsert-general-rates',
  templateUrl: './upsert-general-rates.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertGeneralRatesComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly headerService = inject(HeaderService);
  private readonly translateDecorator = inject(TranslateDecoratorService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly notificationPanelService = inject(NotificationPanelService);
  private readonly securityService = inject(SecurityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly generalRatesRuleEngineService = inject(
    GeneralRatesRuleEngineService
  );

  private readonly allowedSecurityTypeNames: string[] = [
    'Corporate Debt',
    'Government Debt',
    'Listed Equity',
    'Unlisted Equity',
  ];

  public generalRateRules: WritableSignal<GeneralRatesRuleEngine[]> = signal<
    GeneralRatesRuleEngine[]
  >([]);
  public matchedGeneralRateRules: WritableSignal<GeneralRatesRuleEngine[]> =
    signal<GeneralRatesRuleEngine[]>([]);
  public selectedEntityType: WritableSignal<EntityType | null> =
    signal<EntityType | null>(null);
  public selectedInvestmentJurisdiction: WritableSignal<InvestmentJurisdictionShort | null> =
    signal<InvestmentJurisdictionShort | null>(null);
  public selectedSecurityType: WritableSignal<SecurityType | null> =
    signal<SecurityType | null>(null);
  public formValue: WritableSignal<SecurityForm | null> = signal(null);
  public securityRequest: WritableSignal<CreateSecurityRequest | null> =
    signal(null);

  @Input() transactionType!: string;
  @Input() securityId!: string;

  @ViewChild('rulesEngineForm')
  rulesEngineForm?: RulesEngineViewComponent;

  state = new UpsertSecurityState(TransactionType.GeneralRates.toString(), '');
  securityTitle!: string;
  tooltipMessage!: string;
  translate?: TranslateDecoratorTemplate;

  isUpsertFormSecurityEnabled: Signal<boolean> = computed(
    () => !!this.headerService.selectedClient()
  );

  isContinueEnabled: Signal<boolean> = computed(
    () =>
      !!this.selectedEntityType() &&
      !!this.selectedInvestmentJurisdiction() &&
      !!this.selectedSecurityType() &&
      !this.formValue()
  );

  hasNoQuestions: Signal<boolean> = computed(
    () => this.securityRequest()?.questionAnswers?.length === 0
  );

  constructor() {
    const securityTitleTemplate: JSONObject = {
      securityTitle: [
        'requestSpecificAdviceTitle',
        'generalRatesTitle',
        'editRequestSpecificAdviceTitle',
        'editGeneralRatesTitle',
      ],
    };

    const transactionTypeTemplate = {
      transactionType: [
        'requestSpecificAdvice',
        'generalRates',
        'requestSpecificAdvice',
        'generalRates',
      ],
    };

    this.translate = this.translateDecorator
      .path(['upsertSecurity'])
      .assignTemplates([securityTitleTemplate, transactionTypeTemplate])
      .decorator as TranslateDecoratorTemplate;
  }

  ngOnInit(): void {
    if (!this.transactionType) {
      this.transactionType = TransactionType.GeneralRates.toString();
    }

    this.state = new UpsertSecurityState(this.transactionType, this.securityId);
    if (this.translate) {
      this.securityTitle = this.translate('securityTitle', this.state.mode);
      this.tooltipMessage = this.translate('transactionType', this.state.mode);
    }

    if (this.state.isUpdateGeneralRates || this.state.isUpdateSpecificAdvice) {
      this.headerService.disable();
    }

    this.loadGeneralRateRules();

    this.headerService.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resetMatchingSelections();
        this.resetEvaluation();
        this.loadGeneralRateRules();
      });
  }

  ngOnDestroy(): void {
    this.headerService.enable();
    if (!this.headerService.isClientManuallySelected()) {
      this.headerService.clearSelection();
    }
  }

  handleSuccessAdded(securityResponseAudit: SecurityResponseAuditable): void {
    this.notificationPanelService.reloadPendingApprovalCounter();
    this.appNotificationService.crudNotify(
      CrudOperation.Create,
      this.tooltipMessage,
      true
    );
    this.router.navigate([
      '/portfolio',
      securityResponseAudit.securityId,
      'details',
    ]);
  }

  handleSuccessfulUpdate(): void {
    this.notificationPanelService.reloadPendingApprovalCounter();
    this.appNotificationService.crudNotify(
      CrudOperation.Update,
      this.tooltipMessage,
      true
    );
    this.router.navigate(['/portfolio', this.securityId, 'details']);
  }

  continue(): void {
    this.evaluateMatchingGeneralRateRules();
    const formValue = this.buildGeneralRateFormValue();
    if (!formValue) {
      return;
    }

    this.formValue.set(formValue);
  }

  setFormCompleted(
    request: CreateSecurityRequest | UpdateSecurityRequest
  ): void {
    if (!(request instanceof CreateSecurityRequest)) {
      return;
    }

    this.applyGeneralRateRuleTaxRates(request);
    this.securityRequest.set(request);

    if (this.hasNoQuestions()) {
      this.createSecurity();
    }
  }

  createSecurity(): void {
    const request = this.securityRequest();
    if (!request) {
      return;
    }

    this.securityService
      .createSecurity(request)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((securityResponseAudit: SecurityResponseAuditable) => {
        this.handleSuccessAdded(securityResponseAudit);
      });
  }

  resetEvaluation(): void {
    this.rulesEngineForm?.resetSchema();
    this.formValue.set(null);
    this.securityRequest.set(null);
  }

  cancel(): void {
    this.router.navigate(['/portfolio']);
  }

  selectEntityType(entityType: EntityType | null | undefined): void {
    this.selectedEntityType.set(entityType ?? null);
    this.evaluateMatchingGeneralRateRules();
  }

  clearEntityType(): void {
    this.selectedEntityType.set(null);
    this.resetMatchingResults();
  }

  selectInvestmentJurisdiction(
    investmentJurisdiction: InvestmentJurisdictionShort | null | undefined
  ): void {
    this.selectedInvestmentJurisdiction.set(investmentJurisdiction ?? null);
    this.evaluateMatchingGeneralRateRules();
  }

  clearInvestmentJurisdiction(): void {
    this.selectedInvestmentJurisdiction.set(null);
    this.resetMatchingResults();
  }

  selectSecurityType(securityType: SecurityType | null | undefined): void {
    this.selectedSecurityType.set(securityType ?? null);
    this.evaluateMatchingGeneralRateRules();
  }

  clearSecurityType(): void {
    this.selectedSecurityType.set(null);
    this.resetMatchingResults();
  }

  private loadGeneralRateRules(): void {
    this.generalRatesRuleEngineService
      .get()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(generalRatesResponse => {
        this.generalRateRules.set(cloneDeep(generalRatesResponse.items));
        this.evaluateMatchingGeneralRateRules();
      });
  }

  private evaluateMatchingGeneralRateRules(): void {
    const entityTypeId = this.selectedEntityType()?.entityTypeId;
    const investmentJurisdictionId =
      this.selectedInvestmentJurisdiction()?.investmentJurisdictionId;
    const securityTypeId = this.selectedSecurityType()?.securityTypeId;

    if (!entityTypeId || !investmentJurisdictionId || !securityTypeId) {
      this.resetMatchingResults();
      return;
    }

    const matches = this.generalRateRules().filter(
      (item: GeneralRatesRuleEngine) =>
        item.entityTypeId === entityTypeId &&
        item.investmentJurisdictionIds.includes(investmentJurisdictionId) &&
        item.securityTypeIds.includes(securityTypeId)
    );

    this.matchedGeneralRateRules.set(cloneDeep(matches));

    // console.log('[UpsertGeneralRates] General rate rules match result', {
    //   criteria: {
    //     entityTypeId,
    //     investmentJurisdictionId,
    //     securityTypeId,
    //   },
    //   totalMatches: matches.length,
    //   matchedRuleIds: matches.map(item => item.generalRatesRuleEngineId),
    //   matchedRules: matches,
    // });
  }

  private resetMatchingSelections(): void {
    this.selectedEntityType.set(null);
    this.selectedInvestmentJurisdiction.set(null);
    this.selectedSecurityType.set(null);
    this.resetMatchingResults();
  }

  private resetMatchingResults(): void {
    this.matchedGeneralRateRules.set([]);
  }

  get securityTypeNameFilters(): string[] {
    return this.allowedSecurityTypeNames;
  }

  private buildGeneralRateFormValue(): SecurityForm | null {
    const clientId = this.headerService.selectedClient()?.id;
    const entityType = this.selectedEntityType();
    const investmentJurisdiction = this.selectedInvestmentJurisdiction();
    const securityType = this.selectedSecurityType();

    if (!clientId || !entityType || !investmentJurisdiction || !securityType) {
      return null;
    }

    return {
      entity: null,
      clientId,
      generalRateEntityTypeId: entityType.entityTypeId,
      investmentJurisdiction,
      securityType,
      transactionType: TransactionType.GeneralRates,
      name: this.buildGeneralRateName(
        entityType,
        investmentJurisdiction,
        securityType
      ),
      isinCusip: null,
      acquisitionDate: null,
    };
  }

  private applyGeneralRateRuleTaxRates(request: CreateSecurityRequest): void {
    for (const rule of this.matchedGeneralRateRules()) {
      const incomeTypeId = rule.incomeTypeId;
      const templateRates = this.getGeneralRateRuleRates(rule);
      if (!incomeTypeId || templateRates.length === 0) {
        continue;
      }

      const currentTaxRates = request.taxRates ?? [];
      const matchingTaxRates = currentTaxRates.filter(
        item => item.taxResultItemTypeId === incomeTypeId
      );

      if (matchingTaxRates.length === 0) {
        continue;
      }

      const remainingTaxRates = currentTaxRates.filter(
        item => item.taxResultItemTypeId !== incomeTypeId
      );
      const description = rule.explanation ?? '';
      const updatedTaxRates = templateRates.map((rate, index) => {
        const source = matchingTaxRates[index] ?? matchingTaxRates[0];
        return new CreateSecurityTaxRateRequest({
          ...source,
          rate,
          description,
          isActive: true,
        });
      });

      request.taxRates = [
        ...remainingTaxRates,
        ...updatedTaxRates,
      ] as CreateSecurityTaxRateRequest[];
    }
  }

  private getGeneralRateRuleRates(rule: GeneralRatesRuleEngine): number[] {
    const rates = (rule.rates ?? []).filter(
      (rate): rate is number => rate !== undefined && rate !== null
    );

    return rates.length > 0
      ? rates
      : rule.rate !== undefined && rule.rate !== null
        ? [rule.rate]
        : [];
  }

  private buildGeneralRateName(
    entityType: EntityType,
    investmentJurisdiction: InvestmentJurisdictionShort,
    securityType: SecurityType
  ): string {
    return [
      'General Rates',
      entityType.name,
      investmentJurisdiction.name,
      securityType.name,
    ]
      .filter(Boolean)
      .join(' - ');
  }
}
